// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { FirewallPolicyDeployments } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  FirewallPolicyDeploymentsDeployOptionalParams,
  FirewallPolicyDeploymentsDeployResponse,
} from "../models/index.js";

/** Class containing FirewallPolicyDeployments operations. */
export class FirewallPolicyDeploymentsImpl implements FirewallPolicyDeployments {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class FirewallPolicyDeployments class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Deploys the firewall policy draft and child rule collection group drafts.
   * @param resourceGroupName The name of the resource group.
   * @param firewallPolicyName The name of the Firewall Policy.
   * @param options The options parameters.
   */
  async beginDeploy(
    resourceGroupName: string,
    firewallPolicyName: string,
    options?: FirewallPolicyDeploymentsDeployOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<FirewallPolicyDeploymentsDeployResponse>,
      FirewallPolicyDeploymentsDeployResponse
    >
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<FirewallPolicyDeploymentsDeployResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: { resourceGroupName, firewallPolicyName, options },
      spec: deployOperationSpec,
    });
    const poller = await createHttpPoller<
      FirewallPolicyDeploymentsDeployResponse,
      OperationState<FirewallPolicyDeploymentsDeployResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "azure-async-operation",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Deploys the firewall policy draft and child rule collection group drafts.
   * @param resourceGroupName The name of the resource group.
   * @param firewallPolicyName The name of the Firewall Policy.
   * @param options The options parameters.
   */
  async beginDeployAndWait(
    resourceGroupName: string,
    firewallPolicyName: string,
    options?: FirewallPolicyDeploymentsDeployOptionalParams,
  ): Promise<FirewallPolicyDeploymentsDeployResponse> {
    const poller = await this.beginDeploy(resourceGroupName, firewallPolicyName, options);
    return poller.pollUntilDone();
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const deployOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/firewallPolicies/{firewallPolicyName}/deploy",
  httpMethod: "POST",
  responses: {
    200: {
      headersMapper: Mappers.FirewallPolicyDeploymentsDeployHeaders,
    },
    201: {
      headersMapper: Mappers.FirewallPolicyDeploymentsDeployHeaders,
    },
    202: {
      headersMapper: Mappers.FirewallPolicyDeploymentsDeployHeaders,
    },
    204: {
      headersMapper: Mappers.FirewallPolicyDeploymentsDeployHeaders,
    },
    default: {
      bodyMapper: Mappers.CloudError,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.firewallPolicyName1,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
